import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../../styles/MatchList.css';
import MatchTable from '../../components/MatchTable';
import { apiGet } from '../../../services/apiFetch';
import { formatDateToISO } from '../../../utils/formatters';

const MatchList = () => {
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [completedMatches, setCompletedMatches] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, upcoming, completed, live
  const [currentMatches, setCurrentMatches] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    const dateParam = formatDateToISO(selectedDate);
    const dateQuery = dateParam ? `?date=${encodeURIComponent(dateParam)}` : '';

    const fetchUpcomingMatches = async () => {
      try {
        const { data } = await apiGet(`/api/rl/dashboard/upcoming${dateQuery}`);
        const matchesWithStatus = (data || []).map(match => ({
          ...match,
          match_status: 'upcoming'
        }));
        setUpcomingMatches(matchesWithStatus);
        console.log("MatchList.js: fetched upcoming matches from rimble", matchesWithStatus);
        setLoading(false);
      } catch (error) {
        console.error("MatchList.js: error fetching matches:", error);
        setLoading(false);
      }
    };

    const fetchCompletedMatches = async () => {
      try {
        // Get current date in UTC
        const utcNow = new Date(); // JavaScript Date objects are already in local time, toISOString() converts to UTC

        // If a date is selected, fetch only that date; otherwise fetch past 6 days
        let allMatches = [];
        if (dateParam) {
          const { data } = await apiGet(`/api/rl/dashboard/completed?date=${encodeURIComponent(dateParam)}`);
          allMatches = data || [];
        } else {
          const pastDates = [];
          for (let i = 0; i <= 5; i++) {
            const date = new Date(utcNow);
            date.setDate(date.getDate() - i);
            pastDates.push(date.toISOString().split('T')[0]);
          }
          const apiCalls = pastDates.map(date =>
            apiGet(`/api/rl/dashboard/completed?date=${date}`)
              .then(({ data }) => data || [])
              .catch(() => [])
          );
          const results = await Promise.all(apiCalls);
          allMatches = results.flat();
        }

        // Ensure all matches have the correct status
        const matchesWithStatus = allMatches.map(match => ({
          ...match,
          match_status: 'completed'  // Explicitly set status
        }));

        setCompletedMatches(matchesWithStatus);
        console.log("MatchList.js: fetched completed matches from past 5 days", matchesWithStatus);
      } catch (error) {
        console.error("MatchList.js: error fetching completed matches:", error);
        setLoading(false);
      }
    };

    const fetchLiveMatches = async () => {
      try {
        const { data } = await apiGet(`/api/rl/dashboard/live${dateQuery}`);
        const matchesWithStatus = (data || []).map(match => ({
          ...match,
          match_status: 'live'
        }));
        setLiveMatches(matchesWithStatus);
        console.log("MatchList.js: fetched live matches from rimble", matchesWithStatus);
      } catch (error) {
        console.error("MatchList.js: error fetching matches:", error);
        setLoading(false);
      }
    };

    fetchUpcomingMatches();
    fetchCompletedMatches();
    fetchLiveMatches();
  }, [selectedDate]);

  // Update current matches when filter changes or data is loaded
  useEffect(() => {
    if (filter === 'upcoming') {
      setCurrentMatches(upcomingMatches);
    } else if (filter === 'completed') {
      setCurrentMatches(completedMatches);
    } else if (filter === 'live') {
      setCurrentMatches(liveMatches);
    } else {
      // 'all' - combine all matches
      setCurrentMatches([...upcomingMatches, ...liveMatches, ...completedMatches]);
    }
  }, [filter, upcomingMatches, completedMatches, liveMatches]);


  return (
    <div className="match-list-page">
      <div className="page-header">
        <h1>Matches</h1>
        <Link to="/admin/matches/create" className="create-button">
          Create New Match
        </Link>
      </div>

      <div className="ml-filters-row">
        {/* Status filter buttons */}
        {['all', 'upcoming', 'live', 'completed'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`ml-filter-btn${filter === f ? ' active' : ''}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}

        {/* Spacer */}
        <div className="ml-spacer" />

        {/* Search */}
        <input
          type="text"
          placeholder="Search matches..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="ml-search-input"
        />

        {/* Date picker */}
        <input
          type="date"
          value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
          onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value + 'T00:00:00') : null)}
          className="ml-date-input"
        />
        {selectedDate && (
          <button
            onClick={() => setSelectedDate(null)}
            className="ml-filter-btn"
          >
            Clear
          </button>
        )}
      </div>


      {loading ? (
        <div className="loading-spinner"></div>
      ) : currentMatches.length === 0 ? (
        <div className="no-results">No matches found</div>
      ) : (
        <MatchTable filteredMatches={currentMatches} />
      )}
    </div>
  );
};

export default MatchList;
